/**
 * Cash Flow Page — the visible dashboard.
 *
 * Three views behind one toggle (`?view=`):
 *
 *   overview   — what already happened. Real bank lines from CSV imports.
 *       1. Header bar: title + [Upload CSV] button + account toggles + date range
 *       2. Inflow / Outflow / Net summary cards (internal transfers excluded)
 *       3. Category breakdown bar chart
 *       4. Uncategorised queue
 *       5. Transactions table
 *       6. Import history with Undo
 *
 *   monthflow  — what's coming. Projects household bills, recurring invoices
 *                and Stripe plans across a range, with a running balance.
 *                See components/rhythm/MonthFlowView.tsx.
 *
 *   tax        — the partnership return, GST/BAS and personal tax, built from
 *                the category × scope tags. See components/tax/TaxCentre.tsx.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageTitle } from '../../../_metronic/layout/core';
import { CashFlowProvider, useCashFlow } from './contexts/CashFlowContext';
import { UploadCsvModal } from './components/UploadCsvModal';
import { RulesEditor } from './components/RulesEditor';
import { TaxCentre } from './components/tax/TaxCentre';
import { MonthFlowView } from './components/rhythm/MonthFlowView';
import { RecurringMoneyProvider } from './contexts/RecurringMoneyContext';
import {
  SEED_CATEGORIES,
  getCategoryColour,
  getCategoryLabel,
} from './utils/categories';
import { extractCounterpartyHint } from './utils/rulesEngine';
import { BankTransaction } from './types';
import './CashFlowPage.scss';
import { ReconciliationProvider } from './contexts/ReconciliationContext';
import { ReconcileView, ReconciliationSummary } from './components/ReconcileView';

const breadCrumbs = [
  { title: 'Dashboard', path: '/dashboard', isSeparator: false, isActive: false },
  { title: '', path: '', isSeparator: true, isActive: false },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(n);
}

/** Sum a list of transactions for inflow / outflow / net, excluding
 *  internal transfers (they shouldn't double-count). */
function summarise(transactions: BankTransaction[]) {
  let inflow = 0;
  let outflow = 0;
  for (const t of transactions) {
    if (t.category === 'internal_transfer') continue;
    if (t.amount > 0) inflow += t.amount;
    else outflow += Math.abs(t.amount);
  }
  return { inflow, outflow, net: inflow - outflow };
}

/** Group transactions by category and return descending-by-magnitude
 *  list. Internal transfers excluded. */
function breakdownByCategory(transactions: BankTransaction[], direction: 'in' | 'out') {
  const totals = new Map<string, { total: number; count: number }>();
  for (const t of transactions) {
    if (t.category === 'internal_transfer') continue;
    if (direction === 'in' && t.amount <= 0) continue;
    if (direction === 'out' && t.amount >= 0) continue;
    const cur = totals.get(t.category) || { total: 0, count: 0 };
    cur.total += Math.abs(t.amount);
    cur.count += 1;
    totals.set(t.category, cur);
  }
  return Array.from(totals.entries())
    .map(([category, v]) => ({ category, ...v }))
    .sort((a, b) => b.total - a.total);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SummaryCards({ inflow, outflow, net }: { inflow: number; outflow: number; net: number }) {
  return (
    <div className="row g-4 mb-4">
      <div className="col-md-4">
        <div className="card h-100">
          <div className="card-body">
            <div className="text-muted small text-uppercase fw-semibold">Money In</div>
            <div className="display-6 fw-bold text-success">{formatCurrency(inflow)}</div>
            <div className="text-muted small">Excludes internal transfers</div>
          </div>
        </div>
      </div>
      <div className="col-md-4">
        <div className="card h-100">
          <div className="card-body">
            <div className="text-muted small text-uppercase fw-semibold">Money Out</div>
            <div className="display-6 fw-bold text-danger">{formatCurrency(outflow)}</div>
            <div className="text-muted small">Excludes internal transfers</div>
          </div>
        </div>
      </div>
      <div className="col-md-4">
        <div className="card h-100">
          <div className="card-body">
            <div className="text-muted small text-uppercase fw-semibold">Net</div>
            <div className={`display-6 fw-bold ${net >= 0 ? 'text-success' : 'text-danger'}`}>
              {formatCurrency(net)}
            </div>
            <div className="text-muted small">In − Out</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CategoryBars({
  title,
  rows,
  totalAbs,
}: {
  title: string;
  rows: Array<{ category: string; total: number; count: number }>;
  totalAbs: number;
}) {
  if (rows.length === 0) return null;
  return (
    <div className="card mb-4">
      <div className="card-header py-3">
        <h6 className="mb-0">{title}</h6>
      </div>
      <div className="card-body">
        {rows.map((r) => {
          const pct = totalAbs > 0 ? (r.total / totalAbs) * 100 : 0;
          return (
            <div key={r.category} className="mb-3">
              <div className="d-flex justify-content-between align-items-baseline mb-1">
                <div>
                  <span
                    className="d-inline-block me-2 rounded"
                    style={{ width: 10, height: 10, background: getCategoryColour(r.category) }}
                  />
                  <span className="fw-semibold">{getCategoryLabel(r.category)}</span>
                  <span className="text-muted small ms-2">({r.count})</span>
                </div>
                <div className="fw-semibold">{formatCurrency(r.total)}</div>
              </div>
              <div className="progress" style={{ height: 8 }}>
                <div
                  className="progress-bar"
                  style={{
                    width: `${pct.toFixed(2)}%`,
                    background: getCategoryColour(r.category),
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CategorySelect({
  value,
  onChange,
  size = 'sm',
}: {
  value: string;
  onChange: (next: string) => void;
  size?: 'sm' | 'md';
}) {
  return (
    <select
      className={`form-select${size === 'sm' ? ' form-select-sm' : ''}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <optgroup label="Income">
        {SEED_CATEGORIES.filter((c) => c.bucket === 'income').map((c) => (
          <option key={c.id} value={c.id}>{c.label}</option>
        ))}
      </optgroup>
      <optgroup label="Expense">
        {SEED_CATEGORIES.filter((c) => c.bucket === 'expense').map((c) => (
          <option key={c.id} value={c.id}>{c.label}</option>
        ))}
      </optgroup>
      <optgroup label="Other">
        <option value="internal_transfer">Internal Transfer</option>
        <option value="uncategorised">Uncategorised</option>
      </optgroup>
    </select>
  );
}

/** Pick a sensible default rule-pattern from a description. Uses the
 *  counterparty hint (strips ANZ's prefix noise) and then takes the
 *  first 1-3 ALL-CAPS-ish words — usually the merchant name. The user
 *  can edit before confirming. */
function suggestRulePattern(descriptionRaw: string): string {
  const hint = extractCounterpartyHint(descriptionRaw);
  // Take the first ~3 tokens, stopping at the first "noisy" token
  // (purely numeric, or a location-looking lower-case word).
  const tokens = hint.split(/\s+/).filter(Boolean);
  const out: string[] = [];
  for (const tok of tokens) {
    if (out.length >= 3) break;
    if (/^\d+$/.test(tok)) break;          // bare numbers like "4029357733"
    if (/^[A-Z][A-Z0-9*&.\-]+$/.test(tok)) { out.push(tok); continue; }
    if (out.length === 0) { out.push(tok); continue; }  // accept first token regardless
    break;
  }
  return (out.join(' ') || hint || descriptionRaw).trim();
}

function UncategorisedQueue({ transactions }: { transactions: BankTransaction[] }) {
  const { setCategory, bulkCategorise, addRule, reclassifyAll } = useCashFlow();
  const queue = transactions.filter((t) => t.category === 'uncategorised');
  const [expanded, setExpanded] = useState(false);
  /** Capped at 50 rows by default; 'Reveal all' lifts the cap. */
  const [showAll, setShowAll] = useState(false);
  /** Tick-selection for bulk classification. May contain ids that have
   *  since left the queue (categorised elsewhere) — always intersect
   *  with the live queue before using. */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkCategory, setBulkCategory] = useState('expense:other');
  const [bulkScope, setBulkScope] = useState<'' | 'business' | 'personal'>('');
  const [bulkApplying, setBulkApplying] = useState(false);
  /** Map of txId → { pattern, category } when the user is composing a
   *  rule from a specific row. Only one row at a time is open. */
  const [ruleDraft, setRuleDraft] = useState<{ txId: string; pattern: string; category: string } | null>(null);
  const [savingRule, setSavingRule] = useState(false);

  const visible = showAll ? queue : queue.slice(0, 50);
  const visibleSelectedCount = useMemo(
    () => visible.reduce((n, t) => n + (selected.has(t.id) ? 1 : 0), 0),
    [visible, selected],
  );
  const allVisibleSelected = visible.length > 0 && visibleSelectedCount === visible.length;

  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };
  const toggleAllVisible = () => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (allVisibleSelected) visible.forEach((t) => s.delete(t.id));
      else visible.forEach((t) => s.add(t.id));
      return s;
    });
  };

  const applyBulk = async () => {
    // Intersect with the live queue — a row may have been categorised
    // individually (or by a rule run) since it was ticked.
    const ids = queue.filter((t) => selected.has(t.id)).map((t) => t.id);
    if (!ids.length) { toast.info('Nothing selected.'); return; }
    setBulkApplying(true);
    try {
      const n = await bulkCategorise(ids, {
        category: bulkCategory,
        ...(bulkScope ? { scope: bulkScope } : {}),
      });
      toast.success(`Classified ${n} transaction${n === 1 ? '' : 's'} as ${getCategoryLabel(bulkCategory)}${bulkScope ? ` (${bulkScope === 'personal' ? 'family' : 'business'} books)` : ''}.`);
      setSelected(new Set());
    } catch (e: any) {
      toast.error(e?.message || 'Bulk classification failed');
    } finally {
      setBulkApplying(false);
    }
  };

  const beginMakeRule = (t: BankTransaction) => {
    const cat = t.category === 'uncategorised' ? 'expense:other' : t.category;
    setRuleDraft({
      txId: t.id,
      pattern: suggestRulePattern(t.descriptionRaw),
      category: cat,
    });
  };

  const cancelMakeRule = () => setRuleDraft(null);

  const saveMakeRule = async (alsoReclassify: boolean) => {
    if (!ruleDraft || !ruleDraft.pattern.trim()) return;
    setSavingRule(true);
    try {
      await addRule({
        matchType: 'contains',
        pattern: ruleDraft.pattern.trim(),
        category: ruleDraft.category,
        priority: 60,
        enabled: true,
      });
      if (alsoReclassify) {
        const { updated } = await reclassifyAll();
        toast.success(`Rule created. Reclassified ${updated} transaction(s).`);
      } else {
        toast.success('Rule created.');
      }
      setRuleDraft(null);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to create rule');
    } finally {
      setSavingRule(false);
    }
  };

  if (queue.length === 0) return null;
  return (
    <div className="card mb-4">
      <div
        className="card-header py-3 d-flex justify-content-between align-items-center"
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded((v) => !v)}
      >
        <h6 className="mb-0">
          <i className={`bi bi-chevron-${expanded ? 'down' : 'right'} me-2`} />
          Uncategorised <span className="badge bg-warning ms-2">{queue.length}</span>
        </h6>
        <span className="text-muted small">{expanded ? 'Click to collapse' : 'Click to classify'}</span>
      </div>
      {expanded && (
        <div className="card-body p-0">
          {/* Bulk classification bar — appears once anything is ticked. */}
          {selected.size > 0 && (
            <div className="d-flex flex-wrap align-items-center gap-2 px-3 py-2 border-bottom bg-light-primary bg-opacity-25">
              <span className="fw-semibold small">{queue.filter((t) => selected.has(t.id)).length} selected</span>
              <span className="text-muted small">→ classify as</span>
              <div style={{ width: 220 }}>
                <CategorySelect value={bulkCategory} onChange={setBulkCategory} />
              </div>
              <select
                className="form-select form-select-sm"
                style={{ width: 150 }}
                value={bulkScope}
                onChange={(e) => setBulkScope(e.target.value as any)}
                title="Optionally also assign the books"
              >
                <option value="">books: keep</option>
                <option value="business">books: business</option>
                <option value="personal">books: family</option>
              </select>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={applyBulk}
                disabled={bulkApplying}
              >
                {bulkApplying ? 'Applying…' : 'Apply'}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => setSelected(new Set())}
                disabled={bulkApplying}
              >
                Clear selection
              </button>
            </div>
          )}
          <div className="table-responsive">
            <table className="table table-sm mb-0 align-middle">
              <thead>
                <tr>
                  <th style={{ width: 34 }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={allVisibleSelected}
                      onChange={toggleAllVisible}
                      title={allVisibleSelected ? 'Deselect all visible' : 'Select all visible'}
                    />
                  </th>
                  <th>Date</th>
                  <th className="text-end">Amount</th>
                  <th>Description</th>
                  <th style={{ width: 220 }}>Category</th>
                  <th style={{ width: 110 }}></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((t) => {
                  const draftOpen = ruleDraft?.txId === t.id;
                  return (
                    <React.Fragment key={t.id}>
                      <tr className={selected.has(t.id) ? 'table-active' : undefined}>
                        <td>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selected.has(t.id)}
                            onChange={() => toggleRow(t.id)}
                          />
                        </td>
                        <td className="small">{t.date}</td>
                        <td className={`text-end small ${t.amount < 0 ? 'text-danger' : 'text-success'}`}>
                          {formatCurrency(t.amount)}
                        </td>
                        <td className="small">{t.descriptionRaw}</td>
                        <td>
                          <CategorySelect
                            value={t.category}
                            onChange={(next) => setCategory(t.id, next)}
                          />
                        </td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            title="Create a rule that catches transactions like this one"
                            onClick={() => beginMakeRule(t)}
                            disabled={!!ruleDraft}
                          >
                            <i className="bi bi-magic me-1" /> Rule
                          </button>
                        </td>
                      </tr>
                      {draftOpen && (
                        <tr>
                          <td colSpan={6} className="bg-light-info bg-opacity-25 p-3">
                            <div className="row g-2 align-items-end">
                              <div className="col-md-1 text-end">
                                <i className="bi bi-magic fs-3 text-primary" />
                              </div>
                              <div className="col-md-4">
                                <label className="form-label small mb-1">When description contains</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={ruleDraft!.pattern}
                                  onChange={(e) => setRuleDraft({ ...ruleDraft!, pattern: e.target.value })}
                                />
                                <div className="text-muted small mt-1">
                                  Edit to be more or less specific. Case-insensitive.
                                </div>
                              </div>
                              <div className="col-md-3">
                                <label className="form-label small mb-1">classify as</label>
                                <CategorySelect
                                  value={ruleDraft!.category}
                                  onChange={(next) => setRuleDraft({ ...ruleDraft!, category: next })}
                                />
                              </div>
                              <div className="col-md-4 d-flex gap-2 justify-content-end">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-light"
                                  onClick={cancelMakeRule}
                                  disabled={savingRule}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => saveMakeRule(false)}
                                  disabled={!ruleDraft!.pattern.trim() || savingRule}
                                  title="Save the rule but don't re-process existing transactions"
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  onClick={() => saveMakeRule(true)}
                                  disabled={!ruleDraft!.pattern.trim() || savingRule}
                                  title="Save the rule AND immediately apply it to existing transactions"
                                >
                                  {savingRule ? 'Saving…' : 'Save & apply'}
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {queue.length > 50 && (
            <div className="p-2 text-center border-top">
              {showAll ? (
                <button type="button" className="btn btn-sm btn-light" onClick={() => setShowAll(false)}>
                  Show first 50 only
                </button>
              ) : (
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setShowAll(true)}>
                  Reveal all {queue.length}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Look for an INV-* token in a description. Cheap, just a chip
 *  hint — does NOT auto-link or mark paid. The user clicks it. */
function detectInvoiceRef(descriptionRaw: string): string | null {
  const m = descriptionRaw.match(/\b(INV[-_]?\d+)\b/i);
  return m ? m[1].toUpperCase().replace('_', '-') : null;
}

/** Click-to-cycle books badge: unassigned → business → family → unassigned.
 *  One click beats a dropdown for the high-volume triage workflow the
 *  mixed-use credit card creates. */
function ScopeBadge({ tx }: { tx: BankTransaction }) {
  const { setScope } = useCashFlow();
  const [busy, setBusy] = useState(false);
  const next = tx.scope === 'business' ? 'personal' : tx.scope === 'personal' ? null : 'business';
  const cfg = tx.scope === 'business'
    ? { label: 'business', cls: 'bg-light-primary text-primary border border-primary border-opacity-25' }
    : tx.scope === 'personal'
      ? { label: 'family', cls: 'bg-light-success text-success border border-success border-opacity-25' }
      : { label: '— assign —', cls: 'bg-light text-muted border' };
  return (
    <button
      type="button"
      className={`badge ${cfg.cls}`}
      style={{ cursor: 'pointer', opacity: busy ? 0.5 : 1 }}
      disabled={busy}
      title={`Click to set: ${next === null ? 'unassigned' : next === 'personal' ? 'family' : next}`}
      onClick={async () => {
        setBusy(true);
        try { await setScope(tx.id, next); } finally { setBusy(false); }
      }}
    >
      {cfg.label}
    </button>
  );
}

function TransactionsTable({ transactions }: { transactions: BankTransaction[] }) {
  const { setCategory } = useCashFlow();
  const [pageSize] = useState(50);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [direction, setDirection] = useState<'all' | 'in' | 'out'>('all');

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return transactions.filter((t) => {
      if (direction === 'in' && t.amount <= 0) return false;
      if (direction === 'out' && t.amount >= 0) return false;
      if (!s) return true;
      return (
        (t.descriptionCanonical || t.descriptionRaw || '').toLowerCase().includes(s) ||
        getCategoryLabel(t.category).toLowerCase().includes(s)
      );
    });
  }, [transactions, direction, search]);

  const slice = filtered.slice(page * pageSize, (page + 1) * pageSize);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  React.useEffect(() => { setPage(0); }, [search, direction, transactions]);

  return (
    <div className="card mb-4">
      <div className="card-header py-3 d-flex gap-3 align-items-center flex-wrap">
        <h6 className="mb-0 me-auto">Transactions <span className="text-muted small">({filtered.length})</span></h6>
        <div className="btn-group btn-group-sm" role="group">
          <button className={`btn btn-light${direction === 'all' ? ' active' : ''}`} onClick={() => setDirection('all')}>All</button>
          <button className={`btn btn-light${direction === 'in' ? ' active' : ''}`} onClick={() => setDirection('in')}>In</button>
          <button className={`btn btn-light${direction === 'out' ? ' active' : ''}`} onClick={() => setDirection('out')}>Out</button>
        </div>
        <input
          type="search"
          className="form-control form-control-sm"
          style={{ maxWidth: 260 }}
          placeholder="Search description / category…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-sm mb-0 align-middle">
            <thead>
              <tr>
                <th>Date</th>
                <th>Account</th>
                <th className="text-end">Amount</th>
                <th>Description</th>
                <th>Category</th>
                <th>Books</th>
                <th style={{ width: 160 }}></th>
              </tr>
            </thead>
            <tbody>
              {slice.map((t) => {
                const invRef = detectInvoiceRef(t.descriptionRaw);
                return (
                  <tr key={t.id}>
                    <td className="small">{t.date}</td>
                    <td className="small text-muted">{t.accountId.replace('anz-', '')}</td>
                    <td className={`text-end small ${t.amount < 0 ? 'text-danger' : 'text-success'}`}>
                      {formatCurrency(t.amount)}
                    </td>
                    <td className="small">
                      {t.descriptionRaw}
                      {invRef && (
                        <span className="badge bg-light-primary text-primary ms-2" title="Detected invoice reference">
                          <i className="bi bi-receipt me-1" />{invRef}
                        </span>
                      )}
                      {t.category === 'internal_transfer' && (
                        <span className="badge bg-light-info text-info ms-2" title="Internal transfer — paired">
                          <i className="bi bi-arrow-left-right me-1" />transfer
                        </span>
                      )}
                    </td>
                    <td>
                      <span
                        className="badge"
                        style={{
                          background: getCategoryColour(t.category) + '20',
                          color: getCategoryColour(t.category),
                          border: `1px solid ${getCategoryColour(t.category)}40`,
                        }}
                      >
                        {getCategoryLabel(t.category)}
                      </span>
                    </td>
                    <td>
                      <ScopeBadge tx={t} />
                    </td>
                    <td>
                      <CategorySelect value={t.category} onChange={(next) => setCategory(t.id, next)} />
                    </td>
                  </tr>
                );
              })}
              {slice.length === 0 && (
                <tr><td colSpan={7} className="text-center text-muted py-4">No transactions match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center p-2 border-top">
            <button className="btn btn-sm btn-light" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              ← Prev
            </button>
            <span className="text-muted small">Page {page + 1} of {totalPages}</span>
            <button className="btn btn-sm btn-light" disabled={page >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}>
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ImportHistory() {
  const { batches, undoImport, accounts } = useCashFlow();
  const [undoing, setUndoing] = useState<string | null>(null);
  const accountName = (id: string) => accounts.find((a) => a.id === id)?.displayName || id;

  if (batches.length === 0) return null;
  return (
    <div className="card mb-4">
      <div className="card-header py-3"><h6 className="mb-0">Import History</h6></div>
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-sm mb-0 align-middle">
            <thead>
              <tr>
                <th>File</th>
                <th>Account</th>
                <th>Range</th>
                <th className="text-end">Rows</th>
                <th className="text-end">New / Dup</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id} className={b.reverted ? 'text-muted' : ''}>
                  <td className="small"><code>{b.fileName}</code>{b.state === 'importing' && <small className="d-block text-warning">Incomplete import — undo and import again if the upload was interrupted.</small>}</td>
                  <td className="small">{accountName(b.accountId)}</td>
                  <td className="small">{b.dateRangeStart} → {b.dateRangeEnd}</td>
                  <td className="text-end small">{b.rowCount}</td>
                  <td className="text-end small">{b.insertedCount} / {b.skippedDuplicateCount}</td>
                  <td className="text-end">
                    {b.reverted ? (
                      <span className="badge bg-light-secondary text-secondary">Reverted</span>
                    ) : (
                      <button
                        className="btn btn-sm btn-outline-danger"
                        disabled={undoing === b.id}
                        onClick={async () => {
                          if (!window.confirm(`Undo this import? This will delete ${b.insertedCount} transactions.`)) return;
                          setUndoing(b.id);
                          try { await undoImport(b.id); } catch (e: any) { toast.error(e.message); } finally { setUndoing(null); }
                        }}
                      >
                        {undoing === b.id ? 'Undoing…' : 'Undo'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type ScopeFilter = 'all' | 'business' | 'personal' | 'unassigned';

const SCOPE_FILTERS: Array<{ key: ScopeFilter; label: string }> = [
  { key: 'all',        label: 'All books' },
  { key: 'business',   label: 'Business' },
  { key: 'personal',   label: 'Family' },
  { key: 'unassigned', label: 'Unassigned' },
];

function CashFlowContent() {
  const { accounts, transactions, loading, error } = useCashFlow();
  const [showUpload, setShowUpload] = useState(false);
  const [accountFilter, setAccountFilter] = useState<'all' | string>('all');
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');

  // Apply scope (whose books) + account + date-range filters. Scope is
  // the primary lens — the same data serves the business's accounts and
  // the family's, and every total/chart below recomputes per selection.
  const filtered = useMemo(() => {
    return transactions.filter((t) => {
      if (scopeFilter === 'business' && t.scope !== 'business') return false;
      if (scopeFilter === 'personal' && t.scope !== 'personal') return false;
      if (scopeFilter === 'unassigned' && t.scope) return false;
      if (accountFilter !== 'all' && t.accountId !== accountFilter) return false;
      if (from && t.date < from) return false;
      if (to && t.date > to) return false;
      return true;
    });
  }, [transactions, scopeFilter, accountFilter, from, to]);

  // Counts per scope (respecting account/date filters but not the scope
  // pill itself) so the pills read like a triage queue.
  const scopeCounts = useMemo(() => {
    const counts: Record<ScopeFilter, number> = { all: 0, business: 0, personal: 0, unassigned: 0 };
    for (const t of transactions) {
      if (accountFilter !== 'all' && t.accountId !== accountFilter) continue;
      if (from && t.date < from) continue;
      if (to && t.date > to) continue;
      counts.all++;
      if (t.scope === 'business') counts.business++;
      else if (t.scope === 'personal') counts.personal++;
      else counts.unassigned++;
    }
    return counts;
  }, [transactions, accountFilter, from, to]);

  const { inflow, outflow, net } = useMemo(() => summarise(filtered), [filtered]);
  const expenseBreakdown = useMemo(() => breakdownByCategory(filtered, 'out'), [filtered]);
  const incomeBreakdown  = useMemo(() => breakdownByCategory(filtered, 'in'),  [filtered]);

  return (
    <>
      {/* Header */}
      <div className="card mb-4">
        <div className="card-body py-3 d-flex flex-wrap align-items-center gap-3">
          <div className="me-auto">
            <h2 className="mb-0 fw-bold">Cash Flow</h2>
            <span className="text-muted fs-7">Money in, money out, where it's going.</span>
          </div>
          {/* Whose books — every total/chart below recomputes per pill. */}
          <div className="btn-group" role="group" aria-label="Books">
            {SCOPE_FILTERS.map((s) => (
              <button
                key={s.key}
                type="button"
                className={`btn btn-sm ${scopeFilter === s.key ? 'btn-primary' : 'btn-light'}`}
                onClick={() => setScopeFilter(s.key)}
                title={s.key === 'unassigned' ? 'Rows not yet assigned to either set of books' : undefined}
              >
                {s.label}
                <span className={`ms-1 ${scopeFilter === s.key ? 'opacity-75' : 'text-muted'}`}>({scopeCounts[s.key]})</span>
              </button>
            ))}
          </div>
          <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
            <i className="bi bi-upload me-2" /> Upload CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body py-3 d-flex flex-wrap align-items-center gap-3">
          <div>
            <label className="form-label small mb-1">Account</label>
            <select className="form-select form-select-sm" value={accountFilter} onChange={(e) => setAccountFilter(e.target.value)}>
              <option value="all">All accounts</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.displayName}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label small mb-1">From</label>
            <input type="date" className="form-control form-control-sm" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label className="form-label small mb-1">To</label>
            <input type="date" className="form-control form-control-sm" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          {(from || to || accountFilter !== 'all') && (
            <div className="align-self-end">
              <button className="btn btn-sm btn-light" onClick={() => { setFrom(''); setTo(''); setAccountFilter('all'); }}>
                Clear filters
              </button>
            </div>
          )}
          <div className="ms-auto text-muted small align-self-end">
            {filtered.length} of {transactions.length} transactions
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="text-muted">Loading…</div>
      ) : (
        <>
          <SummaryCards inflow={inflow} outflow={outflow} net={net} />
          <UncategorisedQueue transactions={filtered} />
          <RulesEditor />
          <div className="row g-4">
            <div className="col-lg-6">
              <CategoryBars title="Expenses by Category" rows={expenseBreakdown} totalAbs={outflow} />
            </div>
            <div className="col-lg-6">
              <CategoryBars title="Income by Source" rows={incomeBreakdown} totalAbs={inflow} />
            </div>
          </div>
          <TransactionsTable transactions={filtered} />
          <ImportHistory />
        </>
      )}

      <UploadCsvModal show={showUpload} onHide={() => setShowUpload(false)} />
    </>
  );
}

/** The three questions this page answers.
 *
 *  `overview` is history (real bank lines), `monthflow` is the forecast (things
 *  that repeat), `tax` is the return. Deliberately separate views rather than
 *  one merged table: mixing a projected payment into a list of settled bank
 *  transactions is how a forecast gets mistaken for a fact. */
type PageView = 'overview' | 'reconcile' | 'monthflow' | 'tax';

function parseView(raw: string | null): PageView {
  if (raw === 'reconcile') return 'reconcile';
  if (raw === 'tax') return 'tax';
  if (raw === 'monthflow') return 'monthflow';
  return 'overview';
}

/** Segmented switch across the three views.
 *  Self-contained; styled in CashFlowPage.scss. */
function ViewToggle({ view, onChange }: { view: PageView; onChange: (v: PageView) => void }) {
  return (
    <div className="cashflow-view-toggle mb-4">
      <button
        type="button"
        className={`toggle-option overview ${view === 'overview' ? 'active' : ''}`}
        onClick={() => onChange('overview')}
      >
        <i className="bi bi-cash-stack" /> Overview
        <span className="toggle-subtitle">In / Out</span>
      </button>
      <button type="button" className={`toggle-option reconcile ${view === 'reconcile' ? 'active' : ''}`} onClick={() => onChange('reconcile')}><i className="bi bi-link-45deg" /> Reconcile<span className="toggle-subtitle">ANZ</span></button>
      <button type="button" className={`toggle-option monthflow ${view === 'monthflow' ? 'active' : ''}`}
        onClick={() => onChange('monthflow')}
      >
        <i className="bi bi-graph-up-arrow" /> Forecast
        <span className="toggle-subtitle">What&apos;s coming</span>
      </button>
      <button
        type="button"
        className={`toggle-option tax ${view === 'tax' ? 'active' : ''}`}
        onClick={() => onChange('tax')}
      >
        <i className="bi bi-calculator" /> Tax &amp; GST
        <span className="toggle-subtitle">Partnership</span>
      </button>
    </div>
  );
}

/** Holds the view state (URL-synced) and switches between the three views.
 *
 *  Overview and Tax share the one CashFlowProvider so the tax view reuses the
 *  already-loaded transactions. Month Flow adds its own provider — it reads
 *  entirely different collections, and mounting it lazily keeps its three
 *  Firestore reads off the critical path for the two views that don't need
 *  them. */
function CashFlowViews() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState<PageView>(() => parseView(searchParams.get('view')));

  const change = (v: PageView) => {
    setView(v);
    // Month Flow owns `from`/`to`/`scenario` and writes them straight back on
    // mount, so leaving them behind here would flash a stale range.
    setSearchParams(v === 'overview' ? {} : { view: v });
  };

  useEffect(() => {
    setView(parseView(searchParams.get('view')));
  }, [searchParams]);

  return (
    <div className="cashflow-page">
      <ViewToggle view={view} onChange={change} />
      {view === 'overview' && <><ReconciliationSummary onReconcile={() => change('reconcile')} /><CashFlowContent /></>}
      {view === 'reconcile' && <ReconcileView />}
      {view === 'monthflow' && (
        <RecurringMoneyProvider>
          <MonthFlowView />
        </RecurringMoneyProvider>
      )}
      {view === 'tax' && <TaxCentre onGoToOverview={() => change('overview')} />}
    </div>
  );
}

export function CashFlowPage() {
  return (
    <CashFlowProvider>
      <PageTitle breadcrumbs={breadCrumbs}>Cash Flow</PageTitle>
      <ReconciliationProvider><CashFlowViews /></ReconciliationProvider>
    </CashFlowProvider>
  );
}

export default CashFlowPage;
